import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { API } from '../config/api';
import { UserPlus, UserMinus, Loader2 } from 'lucide-react';

export function FollowButton({ 
  targetUserId, 
  initialIsFollowing = false, 
  onFollowChange,
  variant = 'default',
  size = 'default',
  className = ''
}) {
  const { isAuthenticated, getAuthHeaders } = useAuth();
  const navigate = useNavigate();
  const [isFollowing, setIsFollowing] = useState(initialIsFollowing);
  const [loading, setLoading] = useState(false);

  const handleFollow = async (e) => {
    if (e) e.stopPropagation();
    
    if (!isAuthenticated) {
      toast.info('Sign in to follow');
      navigate('/login');
      return;
    }
    
    setLoading(true);
    try {
      const headers = { ...getAuthHeaders(), 'Content-Type': 'application/json' };
      
      if (isFollowing) {
        // Unfollow
        const res = await fetch(`${API}/users/${targetUserId}/unfollow`, {
          method: 'POST',
          headers
        });
        
        if (res.ok) {
          setIsFollowing(false);
          if (onFollowChange) onFollowChange(-1);
          toast.success('Unfollowed');
        }
      } else {
        // Follow
        const res = await fetch(`${API}/users/${targetUserId}/follow`, {
          method: 'POST',
          headers
        });
        
        if (res.ok) {
          const data = await res.json();
          if (data.status === 'following' || data.status === 'already_following') {
            setIsFollowing(true);
            if (onFollowChange) onFollowChange(1);
            toast.success('Following');
          } else if (data.status === 'request_sent') {
            toast.success('Follow request sent');
          }
        }
      }
    } catch (err) {
      toast.error('Failed to update follow');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Button
      variant={isFollowing ? 'outline' : variant}
      size={size}
      className={className}
      onClick={handleFollow}
      disabled={loading}
      data-testid="follow-button"
    >
      {loading ? (
        <Loader2 className="w-4 h-4 animate-spin" />
      ) : isFollowing ? (
        <>
          <UserMinus className="w-4 h-4 mr-2" />
          Following
        </>
      ) : (
        <>
          <UserPlus className="w-4 h-4 mr-2" />
          Follow
        </>
      )}
    </Button>
  );
}

export default FollowButton;
